import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import AnimatedModal from "components/layout/motion/AnimatedModal";
import FunctionalButton from "components/layout/FunctionalButton";

export type PressElevatorButtonArgs = {
  button_description: string;
  approach_offset_m?: number;
  press_depth_m?: number;
  move_duration_s?: number;
};

interface PressElevatorButtonDialogProps {
  visible: boolean;
  onConfirm: (args: PressElevatorButtonArgs) => void;
  onClose: () => void;
}

const parseOptionalFloat = (raw: string): number | undefined => {
  const trimmed = raw.trim();
  if (!trimmed) return undefined;
  const value = Number(trimmed);
  if (!Number.isFinite(value) || value < 0) return undefined;
  return value;
};

const PressElevatorButtonDialog: React.FC<PressElevatorButtonDialogProps> = ({
  visible,
  onConfirm,
  onClose,
}) => {
  const { t } = useTranslation(["missions", "common"]);
  const [buttonDescription, setButtonDescription] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [approachOffset, setApproachOffset] = useState("");
  const [pressDepth, setPressDepth] = useState("");
  const [moveDuration, setMoveDuration] = useState("");

  useEffect(() => {
    if (visible) {
      setButtonDescription("");
      setShowAdvanced(false);
      setApproachOffset("");
      setPressDepth("");
      setMoveDuration("");
    }
  }, [visible]);

  const canConfirm = buttonDescription.trim().length > 0;

  const handleConfirm = () => {
    if (!canConfirm) return;
    const args: PressElevatorButtonArgs = {
      button_description: buttonDescription.trim(),
    };
    const approach = parseOptionalFloat(approachOffset);
    const depth = parseOptionalFloat(pressDepth);
    const duration = parseOptionalFloat(moveDuration);
    if (approach !== undefined) args.approach_offset_m = approach;
    if (depth !== undefined) args.press_depth_m = depth;
    if (duration !== undefined) args.move_duration_s = duration;
    onConfirm(args);
  };

  return (
    <AnimatedModal
      visible={visible}
      onClose={onClose}
      className="w-80 rounded-r10 border border-black80 bg-black100 p-4"
    >
      <p className="mb-3 text-h5 font-semibold">{t("dialogs.pressElevatorTitle")}</p>
      <label className="mb-1 block text-h6 text-black40">
        {t("dialogs.pressElevatorButtonLabel")}
      </label>
      <input
        type="text"
        className="mb-3 w-full rounded-r5 border border-black70 bg-black90 px-3 py-2 text-h5 outline-none focus:border-primary2"
        placeholder={t("dialogs.pressElevatorButtonPlaceholder")}
        value={buttonDescription}
        onChange={(e) => setButtonDescription(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && canConfirm) handleConfirm();
        }}
        autoFocus
      />
      <button
        type="button"
        className="mb-2 text-h6 text-black40 underline hover:text-black20"
        onClick={() => setShowAdvanced((prev) => !prev)}
      >
        {showAdvanced
          ? t("dialogs.pressElevatorHideAdvanced")
          : t("dialogs.pressElevatorShowAdvanced")}
      </button>
      {showAdvanced && (
        <div className="mb-3 space-y-2">
          <label className="block text-h6 text-black40">
            {t("dialogs.pressElevatorApproachOffset")}
            <input
              type="number"
              min={0}
              step="0.01"
              className="mt-1 w-full rounded-r5 border border-black70 bg-black90 px-3 py-1.5 text-h5 outline-none focus:border-primary2"
              placeholder={t("dialogs.pressElevatorServerDefault")}
              value={approachOffset}
              onChange={(e) => setApproachOffset(e.target.value)}
            />
          </label>
          <label className="block text-h6 text-black40">
            {t("dialogs.pressElevatorPressDepth")}
            <input
              type="number"
              min={0}
              step="0.01"
              className="mt-1 w-full rounded-r5 border border-black70 bg-black90 px-3 py-1.5 text-h5 outline-none focus:border-primary2"
              placeholder={t("dialogs.pressElevatorServerDefault")}
              value={pressDepth}
              onChange={(e) => setPressDepth(e.target.value)}
            />
          </label>
          <label className="block text-h6 text-black40">
            {t("dialogs.pressElevatorMoveDuration")}
            <input
              type="number"
              min={0}
              step="0.1"
              className="mt-1 w-full rounded-r5 border border-black70 bg-black90 px-3 py-1.5 text-h5 outline-none focus:border-primary2"
              placeholder={t("dialogs.pressElevatorServerDefault")}
              value={moveDuration}
              onChange={(e) => setMoveDuration(e.target.value)}
            />
          </label>
        </div>
      )}
      <div className="flex justify-end gap-2">
        <FunctionalButton
          title={t("common:cancel")}
          titleClassName="text-black60"
          className="cancel-button px-4 py-2"
          onClick={onClose}
        />
        <FunctionalButton
          title={t("common:add")}
          className="h-7 rounded-r5 bg-primary2 px-4 disabled:cursor-not-allowed disabled:opacity-50"
          disabled={!canConfirm}
          onClick={handleConfirm}
        />
      </div>
    </AnimatedModal>
  );
};

export default PressElevatorButtonDialog;
